import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setToken, getToken, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [privileges, setPrivileges] = useState([]);

  const loadPrivileges = useCallback(async () => {
    try {
      const res = await api.get("/privileges/me");
      setPrivileges(res.data.items || []);
    } catch {
      setPrivileges([]);
    }
  }, []);

  useEffect(() => {
    const t = getToken();
    if (!t) {
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then((res) => {
        setUser(res.data);
        return loadPrivileges();
      })
      .catch(() => setToken(null))
      .finally(() => setLoading(false));
  }, [loadPrivileges]);

  async function login(email, password) {
    const res = await api.post("/auth/login", { email, password });
    setToken(res.data.access_token);
    setUser(res.data.user);
    await loadPrivileges();
    return res.data.user;
  }

  async function demoLogin() {
    const res = await api.post("/auth/demo-login");
    setToken(res.data.access_token);
    setUser(res.data.user);
    await loadPrivileges();
    return res.data.user;
  }

  async function logout() {
    try {
      await api.post("/auth/logout");
    } catch (_e) {}
    setToken(null);
    setUser(null);
    setPrivileges([]);
  }

  const hasPrivilege = useCallback(
    (key) => user?.role_code === "admin" || privileges.includes(key),
    [user, privileges],
  );

  return (
    <AuthContext.Provider value={{ user, loading, privileges, hasPrivilege, login, demoLogin, logout, formatApiError }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
